import type { OnDestroy, OnInit } from '@angular/core';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { REQUIRED_ERROR } from '@shared/jp/data/inputs';
import type { IOrderData } from '@shared/jp/interfaces';
import type { TCouponData } from '@shared/common/interfaces';
import { Logger } from '@shared/common/logger';
import Subscriber from '@shared/common/subscriber';
import { FirebaseService } from 'src/app/services/firebase.service';
import { StripeService } from '@studiobuki/web-core/lib/stripe';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject } from 'rxjs';

const log = new Logger('SectionCartComponent');

@Component({
  selector: 'app-coupon',
  templateUrl: './coupon.component.html',
  styleUrls: ['./coupon.component.scss'],
})
export class CouponComponent implements OnInit, OnDestroy {
  @Input() orderId!: IOrderData['orderId'];

  @Input() set coupon(coupon: TCouponData | undefined) {
    if (!isEqual(this.coupon, coupon)) {
      this._coupon = coupon;
      this.couponChange.emit(coupon);
    }
  }

  get coupon() {
    return this._coupon;
  }

  private _coupon: TCouponData | undefined = undefined;

  @Output() couponChange: EventEmitter<TCouponData | undefined> =
    new EventEmitter();

  // * couponId FormControl
  public couponIdControl = new FormControl('', {
    validators: [
      Validators.required,
      (control) => {
        const { coupon } = this;

        if (coupon && 'error' in coupon && control.value === coupon.id) {
          return [{ forbidden: true }];
        }

        return null;
      },
    ],
    nonNullable: true,
  });

  public readonly REQUIRED_ERROR = REQUIRED_ERROR;

  private _sub = new Subscriber();

  public loading$ = new BehaviorSubject(true);

  constructor(
    private _firebaseService: FirebaseService,
    private _stripeService: StripeService,
  ) {
    this.couponIdControl.disable();
  }

  async ngOnInit() {
    log.info('init');

    const { orderId } = this;

    let coupon;

    try {
      coupon = await this._firebaseService.loadStripeCoupon(orderId);
    } catch (e) {
      log.info('no coupons yet');
    }

    this._sub.push(
      this.couponChange.subscribe((_coupon) => {
        log.info('couponChange');
        this.couponIdControl.setValue(_coupon?.id || '');

        // if (_coupon && 'valid' in _coupon) {
        //   this.couponIdControl.disable();
        // } else {
        //   this.couponIdControl.enable();
        // }
      }),
    );

    if (coupon) {
      this.coupon = coupon;
      this.couponIdControl.markAsTouched();
    }

    this.couponIdControl.enable();
    this.loading$.next(false);
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  /**
   * Applies coupon to the order by `couponId`
   */
  async redeemCoupon() {
    this.loading$.next(true);
    this.couponIdControl.disable();

    const coupon = await this._stripeService.redeemCoupon(
      this.orderId,
      this.couponIdControl.value,
    );

    this.coupon = coupon;

    this.couponIdControl.enable();
    this.loading$.next(false);
  }

  async removeCoupon() {
    this.loading$.next(true);

    await this._firebaseService.deleteStripeCoupon(this.orderId);

    this.coupon = undefined;

    this.loading$.next(false);
  }
}
